import React, { useMemo, useEffect } from 'react';
import { Container, Box, Typography, Divider, Link, List, ListItem } from '@mui/material';
import { useTranslation } from 'react-i18next';

const sections = [
    { id: 'about', key: 'about' },
    { id: 'account', key: 'account' },
    { id: 'fees', key: 'fees' },
    { id: 'borrowing', key: 'borrowing' },
    { id: 'pickup', key: 'pickup' },
    { id: 'conduct', key: 'conduct' },
    { id: 'liability', key: 'liability' },
    { id: 'changes', key: 'changes' },
    { id: 'contact', key: 'contact' }
];

const TermsOfUse = () => {
  const { t, i18n } = useTranslation();

  const lastUpdated = useMemo(() => {
    try {
      return new Date().toLocaleDateString(i18n.language?.startsWith('uk') ? 'uk-UA' : 'en-CA', {
        year: 'numeric', month: 'long', day: '2-digit'
      });
    } catch {
      return new Date().toISOString().slice(0, 10);
    }
  }, [i18n.language]);

  useEffect(() => {
    document.title = `${t('tos.title')} — ${t('library')}`;
  }, [t]);

  const list = (path) => {
    const val = t(path, { returnObjects: true, defaultValue: [] });
    if (Array.isArray(val)) return val;
    if (val == null) return [];
    if (typeof val === 'string') {
      const s = val.trim();
      // If i18n returned the key itself (missing translation), treat as empty
      return s && s !== path ? [s] : [];
    }
    return [];
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
      <Box textAlign="center" mb={2}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
          {t('tos.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('tos.lastUpdated', { date: lastUpdated })}
        </Typography>
      </Box>

      <Box sx={{ p: 2, bgColor: 'background.paper', borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', mb: 1 }}>
          {t('tos.toc')}
        </Typography>
        <Box component="nav" aria-label={t('tos.toc')}>
          <List dense sx={{ columns: { xs: 1, sm: 2 }, columnGap: 4, listStyle: 'none', p: 0, m: 0 }}>
            {sections.map(({ id, key }) => (
              <ListItem key={id} sx={{ breakInside: 'avoid', display: 'list-item', pl: 0 }}>
                <Link href={`#${id}`} underline="hover">{t(`tos.sections.${key}.title`)}</Link>
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>

      <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>{t('tos.accept')}</Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>{t('tos.notPrivacy')}</Typography>

      <Divider sx={{ my: 2 }} />

      {/* About */}
      <Box id="about" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('tos.sections.about.title')}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>{t('tos.sections.about.body')}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Account */}
      <Box id="account" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('tos.sections.account.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('tos.sections.account.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Fees */}
      <Box id="fees" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('tos.sections.fees.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('tos.sections.fees.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Borrowing */}
      <Box id="borrowing" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('tos.sections.borrowing.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('tos.sections.borrowing.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('tos.sections.borrowing.note')}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Pickup & Return */}
      <Box id="pickup" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('tos.sections.pickup.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('tos.sections.pickup.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Conduct */}
      <Box id="conduct" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('tos.sections.conduct.title')}</Typography>
        <List sx={{ pl: 3 }}>
          {list('tos.sections.conduct.items').map((item, idx) => (
            <ListItem key={idx} sx={{ display: 'list-item', color: 'text.secondary', py: 0.5 }}>{item}</ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Liability */}
      <Box id="liability" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('tos.sections.liability.title')}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>{t('tos.sections.liability.body')}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Changes */}
      <Box id="changes" sx={{ scrollMarginTop: 96, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('tos.sections.changes.title')}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>{t('tos.sections.changes.body')}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Contact */}
      <Box id="contact" sx={{ scrollMarginTop: 96 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('tos.sections.contact.title')}</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>{t('tos.sections.contact.body')}</Typography>
      </Box>
    </Container>
  );
};

export default TermsOfUse;
